/* eslint-disable react/no-array-index-key */

import { MouseEvent, ReactElement, ReactNode } from "react";
import { Descendant } from "slate";

import {
  PlaceholderData,
  isEditorElementType,
  isEditorText,
  ParagraphTag,
  EditorElement,
} from "@smart/itops-serialisation-basic";
import { specialChars } from "@smart/itops-utils-basic";

import { DisplayLink } from "./display-link";
import { DisplayParagraph } from "./display-paragraph";

type ChildrenProps = {
  paragraphTag?: ParagraphTag;
  hintElement?: ReactNode;
  items: Descendant[];
  disableLinks?: boolean;
  placeholderData?: Partial<PlaceholderData>;
  onPlaceholderClick?: (
    e: MouseEvent<HTMLSpanElement>,
    element: EditorElement<"placeholder">,
  ) => void;
};

const isParagraphEmpty = (children: Descendant[]): boolean =>
  children.every(
    (c) => isEditorText(c) && typeof c.text === "string" && !c.text.trim(),
  );

const Children = ({
  paragraphTag,
  hintElement,
  items,
  disableLinks,
  placeholderData,
  onPlaceholderClick,
}: ChildrenProps) => (
  <>
    {items.map((it, index) => {
      if (isEditorElementType("paragraph")(it)) {
        const para = it as EditorElement<"paragraph">;
        const isEmpty =
          !para.children?.length || isParagraphEmpty(para.children);
        return (
          <DisplayParagraph
            Tag={para.paragraphTag ?? paragraphTag}
            headingStyled={!!para.paragraphTag}
            key={index}
          >
            {isEmpty ? (
              "\u00A0"
            ) : (
              <Children
                items={para.children}
                disableLinks={disableLinks}
                placeholderData={placeholderData}
                onPlaceholderClick={onPlaceholderClick}
              />
            )}
            {index === 0 && hintElement}
          </DisplayParagraph>
        );
      }

      if (isEditorElementType("bulleted-list")(it)) {
        return (
          <ul className="list-disc list-inside block m-0 pl-4 my-1" key={index}>
            {it.children.map((item, i) =>
              isEditorElementType("list-item")(item) ? (
                <li className="m-0" key={i}>
                  <Children
                    items={item.children}
                    disableLinks={disableLinks}
                    placeholderData={placeholderData}
                    onPlaceholderClick={onPlaceholderClick}
                  />
                </li>
              ) : null,
            )}
          </ul>
        );
      }

      if (isEditorElementType("numbered-list")(it)) {
        return (
          <ol
            className="list-decimal list-inside block m-0 pl-4 my-1"
            key={index}
          >
            {it.children.map((item, i) =>
              isEditorElementType("list-item")(item) ? (
                <li className="m-0" key={i}>
                  <Children
                    items={item.children}
                    disableLinks={disableLinks}
                    placeholderData={placeholderData}
                    onPlaceholderClick={onPlaceholderClick}
                  />
                </li>
              ) : null,
            )}
          </ol>
        );
      }

      if (isEditorElementType("blockquote")(it)) {
        return (
          <blockquote
            className="border-l-4 border-neutral-400 pl-4 my-1"
            key={index}
          >
            <Children
              items={it.children}
              disableLinks={disableLinks}
              placeholderData={placeholderData}
              onPlaceholderClick={onPlaceholderClick}
            />
          </blockquote>
        );
      }

      if (isEditorElementType("link")(it)) {
        return (
          <DisplayLink element={it} key={index} disableLinks={disableLinks}>
            <Children
              items={it.children}
              disableLinks={disableLinks}
              placeholderData={placeholderData}
              onPlaceholderClick={onPlaceholderClick}
            />
          </DisplayLink>
        );
      }

      if (isEditorElementType("placeholder")(it)) {
        if (it.isLink && disableLinks)
          return (
            <span
              className="underline text-blue-360 text-paragraph"
              key={index}
            >
              {it.displayText || it.name}
            </span>
          );

        return it.isLink ? (
          <a
            href={placeholderData?.[it.name] || it.name}
            target="_blank"
            rel="noreferrer"
            className="underline text-blue-360 text-paragraph"
            onClick={(e) => onPlaceholderClick?.(e, it)}
          >
            {it.displayText || it.name}
          </a>
        ) : (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
          <span key={index} onClick={(e) => onPlaceholderClick?.(e, it)}>
            {placeholderData?.[it.name]}
          </span>
        );
      }

      if (isEditorText(it)) {
        // render indent tabs as non-breaking spaces
        let display: string | ReactElement = it.text.replace(
          /\t/g,
          specialChars.nbsp.repeat(4),
        );
        if (it.bold) display = <strong>{display}</strong>;
        if (it.italic) display = <em>{display}</em>;
        if (it.underline) display = <u>{display}</u>;

        return <span key={index}>{display}</span>;
      }
      return null;
    })}
  </>
);

export { Children, ChildrenProps };
